import React from 'react';
import type { CSSProperties } from 'react';

import { Button } from 'apps/auth/components/common/Button/Button';
import type { ButtonVariant } from 'apps/auth/components/common/Button/Button';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { NavBar } from 'apps/auth/components/common/Layout/NavBar/NavBar';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Text } from 'apps/auth/components/common/Text/Text';
import { useMatchMedia } from 'apps/auth/hooks/useMatchMedia';
import breakpoints from 'apps/auth/theme/breakpoints';
import clsx from 'clsx';

import styles from './StoriesLayout.module.css';

interface LinkInfo {
    info?: React.ReactNode;
    link_url?: string;
    link_name?: string;
}

interface StoryLayoutProps {
    topButtonCaption?: string;
    onTopButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    imgA?: string;
    imgA2x?: string;
    imgB?: string;
    imgB2x?: string;
    bottomButtonCaption?: string;
    href?: string;
    onBottomButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
    variant?: ButtonVariant;
    contentPosition?: 'top' | 'middle' | 'bottom';
    largeTitle?: boolean;
    fullwidthImg?: boolean;
    linkInfo?: LinkInfo;
}

const getFullwidthBgStyle = (props: StoryLayoutProps, isTablet: boolean): CSSProperties | undefined => {
    if (!props.fullwidthImg) return undefined;
    const isRetina = typeof window !== 'undefined' && window.devicePixelRatio > 1;
    const imgA = (isRetina && props.imgA2x) || props.imgA;
    const imgB = (isRetina && props.imgB2x) || props.imgB;
    const bg = (isTablet && imgB) || imgA;
    return bg ? { backgroundImage: `url(${bg})` } : undefined;
};

const StoryLayout: React.FC<StoryLayoutProps> = (props) => {
    const isTablet = useMatchMedia(breakpoints.tablet720);

    const containerClass = clsx(styles.storyContainer, props.fullwidthImg && styles.fullwidthImg, `bgPrimary`);

    const contentClass = clsx(
        styles.storyContent,
        props.contentPosition === 'top' && styles.contentTop,
        props.fullwidthImg && styles.contentFullwidthImg,
        props.contentPosition === 'bottom' && styles.contentBottom,
    );

    const infoClass = clsx(styles.storyInfo, props.contentPosition === 'middle' && styles.infoMiddle);

    const imgClass = clsx(styles.storyImg, props.contentPosition === 'middle' && styles.imgMiddle, props.contentPosition === 'top' && styles.imgBottom);

    return (
        <div className={styles.storiesPopup}>
            <div className={containerClass} style={getFullwidthBgStyle(props, isTablet)}>
                {props.topButtonCaption && (
                    <NavBar align="right" insideStories>
                        <Button caption={props.topButtonCaption} variant="glassTertiary" size="tight" type="button" onClick={props.onTopButtonClick} />
                    </NavBar>
                )}
                <div className={contentClass}>
                    <div className={infoClass}>
                        <div className={styles.storyTitle}>
                            <Text variant="div" size={props.largeTitle ? 'display1' : 'header'} color="white_base" textAlign="center">
                                {props.title}
                            </Text>
                        </div>
                        <Text variant="div" size="main" color="white_base" textAlign="center">
                            {props.subtitle}
                        </Text>
                    </div>
                    {!props.fullwidthImg && (
                        <picture className={imgClass}>
                            {props.imgB && (
                                <source data-size="b" media={`(min-width: ${breakpoints.tablet720})`} srcSet={`${props.imgB} 1x, ${props.imgB2x ?? ''} 2x`} />
                            )}
                            <source data-size="a" srcSet={`${props.imgA ?? ''} 1x, ${props.imgA2x ?? ''} 2x`} />
                            <img src={props.imgB ? `${props.imgB}` : `${props.imgA}`} alt="story" />
                        </picture>
                    )}
                    {props.linkInfo && (
                        <div className={styles.note}>
                            <Text variant="div" size="note" color="superLightGray" textAlign="center" indent="8px">
                                {props.linkInfo.info}
                            </Text>
                            <Row textAlign="center">
                                <Text
                                    href={props.linkInfo.link_url}
                                    size="note"
                                    variant="link"
                                    color="superLightGray"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    indent="8px"
                                >
                                    {props.linkInfo.link_name}
                                </Text>
                            </Row>
                        </div>
                    )}
                </div>
                <FixedBottomPanel isRightAligned insideStories>
                    <Button
                        caption={props.bottomButtonCaption}
                        variant={props.variant}
                        type="link"
                        href={props.href}
                        onClick={props.onBottomButtonClick}
                        mobileFullwidth
                    />
                </FixedBottomPanel>
            </div>
        </div>
    );
};

export default StoryLayout;
